/**
 * The rest of GTM: the data-driven browse pages and the content pages.
 *
 * `/incidents/{slug}` and `/risk-reports/{id}` are NOT here — those two are rendered by FastAPI at
 * request time from templates this build emits, because their links are handed over the instant
 * they are created and a prebuilt site would 404 the share at step one. See `templates.tsx`.
 */
import { useEffect, useState } from "react"
import {
  registry,
  type IncidentPage,
  type ProfileKindSection,
  type StateOfRisk,
} from "@agitome/ui-shared"
import { Shell } from "@agitome/ui-shared"
import { Async, Bar, Capture, Rules, StandingBadge, TierPill } from "@agitome/ui-shared"
import { preloaded, useAsync } from "@agitome/ui-shared"

// ── /tools ─────────────────────────────────────────────────────────────────

const TOOLS: [string, string, string][] = [
  ["/tools/incidents", "The incident database", "Every reported agent failure, filterable by framework, model, category, vendor or DID."],
  ["/tools/risk-profiles", "Risk profiles", "One page per subject. “Is LangChain risky to deploy?” — counts, failure modes, tier, and the rules behind it."],
  ["/tools/categories", "Browse by failure mode", "The closed taxonomy every product classifies against, so counts are comparable across subjects."],
  ["/tools/gate", "The admission gate", "Your policy over reported data — allow, review or deny, before it reaches production."],
  ["/tools/risk-report", "Pull a risk report", "One shareable report across your whole stack. A snapshot: the link shows what you saw."],
  ["/tools/exposure", "Exposure calculator", "What a failure could cost you, and an honest answer on whether it is insurable yet."],
  ["/tools/state-of-agent-risk", "State of Agent Risk", "The periodic public drop off the whole corpus, methodology printed beside it."],
  ["/tools/track-record", "Verify a track record", "Check a signed track-record export offline — without trusting agitome to tell you the truth."],
  ["/tools/report", "File an incident", "Two fields, under a minute, no account."],
  ["/tools/vendors", "Dispute or claim", "This page is about your product. Four things you can do, three needing no account."],
  ["/tools/overlay", "Embed the risk badge", "A flat, cacheable badge for your own directory or docs. An image, not a script runtime."],
  ["/tools/oracle", "On-chain attestations", "How ERC-8004 attestations work, and what agitome does and does not assert in one."],
]

export function Tools() {
  return (
    <Shell>
      <div className="band band--ink band--mesh band--grain">
        <div className="wrap">
          <p className="live">Every one of these is free and needs no account</p>
          <h1 className="mega mt-4 balance" style={{ maxWidth: "14ch" }}>Free <span className="hot">tools</span>.</h1>
          <p className="lede mt-6 measure">
            Each one is a real page with a real URL. Share any of them; the link shows the reader
            exactly what you saw.
          </p>
        </div>
      </div>
      <div className="band band--paper">
        <div className="wrap">
          <div className="grid grid-3">
            {TOOLS.map(([href, title, blurb], i) => (
              <a className="card tool" href={href} key={href}>
                <p className="tool__n">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="h3 mt-3">{title}</h3>
                <p className="fine mt-3">{blurb}</p>
              </a>
            ))}
          </div>
        </div>
      </div>
      <div className="band band--ink band--mesh">
        <div className="wrap center">
          <p className="eyebrow eyebrow--accent">Stay on top of this</p>
          <h2 className="display mt-4 balance" style={{ marginInline: "auto", maxWidth: "20ch" }}>
            One email a month, on what broke and what got fixed.
          </h2>
          <div className="capture mt-6" style={{ maxWidth: "30rem", marginInline: "auto" }}>
            <Capture source="newsletter" />
          </div>
        </div>
      </div>
    </Shell>
  )
}

// ── /incidents ─────────────────────────────────────────────────────────────

export function Incidents() {
  const [filters, setFilters] = useState<Record<string, string>>({ sort: "newest" })
  // Seeded by the prerenderer so the catalog is IN the document a crawler reads. Filtering then
  // refetches; the unfiltered first page is the one that has to exist without JavaScript.
  const [page, setPage] = useState<IncidentPage | null>(preloaded<IncidentPage>("incidents"))
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    registry
      .incidents({ ...filters, limit: 25 })
      .then(setPage)
      .catch((err: Error) => setError(err.message))
  }, [filters])

  const set = (k: string, v: string) => setFilters((f) => ({ ...f, [k]: v }))

  return (
    <Shell current="/incidents">
      <div className="band band--ink band--mesh band--grain">
        <div className="wrap">
          <p className="live">
            {page
              ? `${page.by_standing.reported.toLocaleString("en-US")} reported · ` +
                `${page.by_standing.verified.toLocaleString("en-US")} adjudicated`
              : "The public registry"}
          </p>
          <h1 className="mega mt-4 balance" style={{ maxWidth: "13ch" }}>
            The agent incident <span className="hot">database</span>.
          </h1>
          <p className="lede mt-6 measure">
            Everything the public has reported about named agents, frameworks and models. Most of
            it is unverified — every row says which.
          </p>
          <div className="row mt-6">
            <a className="btn btn--primary btn--lg" href="/tools/report">File an incident</a>
            <a className="btn btn--ghost btn--lg" href="/tools/risk-profiles">Browse by subject</a>
          </div>
        </div>
      </div>

      <div className="band band--paper">
        <div className="wrap">
          <div className="row mt-4">
            <input className="field" style={{ maxWidth: "16rem" }} placeholder="Search titles…"
              onChange={(e) => set("q", e.target.value)} />
            <input className="field" style={{ maxWidth: "12rem" }} placeholder="Framework"
              onChange={(e) => set("framework", e.target.value)} />
            {/* aria-label, because this filter row has no visible label by design — the
                first option carries the meaning ("Any standing"). That works for somebody
                looking at it and not at all for somebody hearing it: a screen reader announces
                "combobox, Any standing" with nothing to say what it filters. The two inputs
                beside it get theirs from `placeholder`, which the accessible-name computation
                does accept; a <select> has no placeholder, which is why these two were the
                only controls in the row that were nameless. */}
            <select className="field" style={{ maxWidth: "12rem" }} aria-label="Filter by standing"
              onChange={(e) => set("standing", e.target.value)}>
              <option value="">Any standing</option>
              <option value="reported">Reported</option>
              <option value="verified">Verified</option>
            </select>
            <select className="field" style={{ maxWidth: "12rem" }} aria-label="Sort order"
              onChange={(e) => set("sort", e.target.value)}>
              <option value="newest">Most recent</option>
              <option value="oldest">Oldest</option>
              <option value="severity">Most severe</option>
            </select>
          </div>

          {error ? (
            <div className="well mt-6" role="alert">
              <strong>Could not load the registry.</strong>
              <p className="fine mt-2">{error}</p>
              <p className="fine mt-2">
                This is a failure to reach agitome — it is not "no incidents have been reported".
              </p>
            </div>
          ) : !page ? (
            <p className="fine muted mt-6">Loading…</p>
          ) : page.items.length === 0 ? (
            <p className="fine muted mt-6">Nothing matches that filter.</p>
          ) : (
            <>
              <p className="eyebrow mt-6">
                Showing {page.items.length} of {page.total.toLocaleString("en-US")}
              </p>
              <div className="tablewrap mt-4">
                <table className="table-stack">
                  <thead>
                    <tr><th>Incident</th><th>Standing</th><th>Severity</th><th>Subject</th><th>Filed</th></tr>
                  </thead>
                  <tbody>
                    {page.items.map((i) => (
                      <tr key={i.incident_id}>
                        <td data-l="Incident"><a href={`/tools/incidents/${i.slug}`}>{i.title}</a></td>
                        <td data-l="Standing"><StandingBadge standing={i.standing} /></td>
                        <td className="num" data-l="Severity">
                          {i.classification.severity} · {i.classification.reason_class}
                        </td>
                        <td data-l="Subject">
                          {i.subject.framework ?? i.subject.model ?? i.subject.agent_category ?? i.subject.vendor ?? "—"}
                        </td>
                        <td className="num" data-l="Filed">{i.reported_at.slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="fine mt-6">{page.standing_note}</p>
              <p className="fine mt-3">
                Withdrawn and disputed reports stay listed and are excluded from every published
                statistic while contested. Hiding a contested claim is what makes a registry
                unauditable.
              </p>
            </>
          )}
        </div>
      </div>
    </Shell>
  )
}

// ── /risk-profiles and /risk-profiles/{kind}/{slug} ────────────────────────

export function ProfileIndex() {
  const kinds = useAsync(() => registry.profileKinds(), [], "profileKinds")

  return (
    <Shell current="/risk-profiles">
      <div className="band band--violet band--grain">
        <div className="wrap">
          <p className="live">One page per framework, model and agent category</p>
          <h1 className="mega mt-4 balance" style={{ maxWidth: "14ch" }}>
            Every subject we <span className="hot">track</span>.
          </h1>
          <p className="lede mt-6 measure">
            A page exists before anyone has filed against its subject, so there is somewhere to
            look — and it carries no tier until real reports arrive.
          </p>
        </div>
      </div>
      <div className="band band--paper">
        <div className="wrap">
          <Async state={kinds}>
            {(data) =>
              data.kinds.map((section: ProfileKindSection) => (
                <section key={section.subject_kind} style={{ marginBottom: "var(--s8)" }}>
                  <div className="row row--between">
                    <h2 className="h2">{section.subject_kind.replace(/_/g, " ")}</h2>
                    <p className="fine">{section.total.toLocaleString("en-US")} tracked</p>
                  </div>
                  <div className="tablewrap mt-4">
                    <table className="table-stack">
                      <thead>
                        <tr><th>Subject</th><th>Tier</th><th className="num">Reported</th>
                          <th className="num">Verified</th><th className="num">Resolved</th><th>Origin</th></tr>
                      </thead>
                      <tbody>
                        {section.subjects.map((s) => (
                          <tr key={s.subject_slug}>
                            <td data-l="Subject"><a href={s.path}>{s.display_name}</a></td>
                            <td data-l="Tier"><TierPill tier={s.tier} /></td>
                            <td className="num" data-l="Reported">{s.incidents}</td>
                            <td className="num" data-l="Verified">{s.verified}</td>
                            <td className="num" data-l="Resolved">{s.resolved}</td>
                            <td data-l="Origin"><span className="fine">{s.origin}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ))
            }
          </Async>
          <div className="slab mt-8">
            <p>
              <strong>"Insufficient" is not a clean bill of health.</strong> It is the absence of an
              assessment — too few counted incidents — and it is styled apart from the ladder so no
              sort order can render it as the safe end of one.
            </p>
          </div>
        </div>
      </div>
    </Shell>
  )
}

/** One subject. Reads its kind and slug from the path so one build serves every profile. */
export function Profile({ kind: k, slug: sl }: { kind?: string; slug?: string } = {}) {
  // Props when the prerenderer builds this page for a named subject; the URL when the browser
  // navigated here. Both paths hit the same component, so a prebuilt page and a client-rendered
  // one cannot come to look different.
  const [kind, slug] =
    k && sl
      ? [k, sl]
      : (() => {
          const parts =
            typeof window === "undefined" ? [] : window.location.pathname.split("/").filter(Boolean)
          return [parts[1] ?? "framework", parts.slice(2).join("/")]
        })()
  const profile = useAsync(
    () => registry.profile(kind, slug),
    [kind, slug],
    `profile:${kind}/${slug}`,
  )

  return (
    <Shell current="/risk-profiles">
      <Async state={profile}>
        {(p) => (
          <>
            <div className="band band--ink band--mesh band--grain">
              <div className="wrap">
                <p className="eyebrow"><a href="/tools/risk-profiles">Risk profiles</a> › {p.subject_kind} › {p.display_name}</p>
                <h1 className="mega mt-4 balance" style={{ maxWidth: "15ch" }}>
                  Is {p.display_name} risky to <span className="hot">deploy</span>?
                </h1>
                <div className="row mt-8" style={{ gap: "var(--s7)" }}>
                  <div className="stat"><div className="stat__n" style={{ color: "#ff9a5c" }}>{p.counted.reported}</div><div className="stat__l">Reported</div></div>
                  <div className="stat"><div className="stat__n" style={{ color: "#4fd6c4" }}>{p.counted.verified}</div><div className="stat__l">Verified</div></div>
                  <div className="stat"><div className="stat__n">{p.resolved}</div><div className="stat__l">Resolved</div></div>
                </div>
              </div>
            </div>

            <div className="band band--paper">
              <div className="wrap">
                <div className="row row--between">
                  <div><p className="eyebrow">Risk tier</p><p className="h1 mt-2"><TierPill tier={p.tier} /></p></div>
                  <p className="fine">{p.counted.total} counted incidents</p>
                </div>

                {p.tier === "insufficient_data" ? (
                  <div className="slab mt-6">
                    <p><strong>"Insufficient" is not a clean bill of health.</strong></p>
                    <p className="mt-4">
                      It is the absence of an assessment. agitome will not assign a tier below the
                      report floor, because a tier derived from a handful of reports would be noise
                      wearing the costume of a finding.
                    </p>
                    <p className="mt-4">
                      It is styled apart from the ladder — not at its safe end — so that no sort
                      order anywhere can present it as low risk.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="mt-5">
                      Every rule that fired, with the numbers it read — so you can recompute this
                      from the page rather than trust it.
                    </p>
                    <Rules rules={p.rules} />
                  </>
                )}

                {p.seeded_note ? <p className="fine mt-6">{p.seeded_note}</p> : null}

                {p.failure_modes.length > 0 ? (
                  <>
                    <h2 className="h2 mt-8">What goes wrong</h2>
                    <p className="fine mt-2">
                      In core's taxonomy — the same vocabulary every product in the portfolio
                      classifies against, so these are comparable across subjects.
                    </p>
                    <div className="bars bars--named mt-5">
                      {p.failure_modes.map((m) => (
                        <Bar key={`${m.reason_class}/${m.reason_code}`}
                          label={m.reason_code.replace(/_/g, " ")}
                          sub={m.reason_class}
                          value={m.count}
                          max={Math.max(...p.failure_modes.map((x) => x.count), 1)} />
                      ))}
                    </div>
                  </>
                ) : null}

                {Object.keys(p.severity_mix).length > 0 ? (
                  <>
                    <h2 className="h2 mt-8">Severity mix</h2>
                    <p className="fine mt-2">
                      Counted incidents only. Every level is shown including the empty ones, so the
                      shape is not flattered by omission.
                    </p>
                    <div className="bars mt-5">
                      {Object.entries(p.severity_mix).map(([level, count]) => (
                        <Bar key={level} label={`sev ${level}`} value={count}
                          max={Math.max(...Object.values(p.severity_mix), 1)} />
                      ))}
                    </div>
                  </>
                ) : null}

                <div className="card card--flat mt-6" style={{ borderStyle: "dashed" }}>
                  <p className="fine">
                    <strong>Reported is not verified.</strong> {p.counted.reported} of these{" "}
                    {p.counted.total} are unverified signals filed by the public. A report is not a
                    finding, and it moves no agent's score.
                  </p>
                  <p className="fine mt-3">
                    <strong>Withdrawn and disputed reports are excluded.</strong>{" "}
                    {p.excluded_from_statistics} filed report(s) are not counted here. They stay
                    visible — hiding a contested claim is what makes a registry unauditable.
                  </p>
                  <p className="fine mt-3">
                    <strong>There is no incident rate here, and there will not be.</strong> A rate
                    needs a denominator, and nobody reports one.
                  </p>
                </div>

                <div className="row mt-8">
                  <a className="btn btn--primary" href="/tools/risk-report">Pull a risk report</a>
                  <a className="btn btn--ghost" href="/tools/gate">Run the admission gate</a>
                  {!p.subject_claimed ? (
                    <a className="btn btn--ghost" href="/tools/vendors">Claim this subject</a>
                  ) : null}
                </div>
              </div>
            </div>
          </>
        )}
      </Async>
    </Shell>
  )
}

// ── /categories ────────────────────────────────────────────────────────────

export function Categories() {
  const taxonomy = useAsync(() => registry.taxonomy(), [], "taxonomy")
  const drop = useAsync(() => registry.stateOfRisk(), [], "drop")

  return (
    <Shell current="/categories">
      <div className="band band--acid band--grain">
        <div className="wrap">
          <p className="eyebrow">The taxonomy</p>
          <h1 className="mega mt-4 balance" style={{ maxWidth: "13ch" }}>What actually goes wrong.</h1>
          <p className="lede mt-6 measure">
            A closed vocabulary, owned by core and shared by every product in the portfolio. Fetch
            it — never hardcode it. Because everyone classifies against the same list, counts are
            comparable across subjects that have nothing else in common.
          </p>
        </div>
      </div>
      <div className="band band--paper">
        <div className="wrap">
          <Async state={taxonomy}>
            {(t) => {
              const classes = (t.reason_classes ?? {}) as Record<string, { codes: string[]; fault: string }>
              const counts = new Map<string, number>()
              for (const m of drop.data?.failure_modes ?? []) {
                counts.set(m.reason_class, (counts.get(m.reason_class) ?? 0) + m.count)
              }
              return (
                <div className="grid grid-2">
                  {Object.entries(classes).map(([name, spec]) => (
                    <div className="card" key={name}>
                      <div className="row row--between">
                        <h2 className="h2">{name.replace(/_/g, " ")}</h2>
                        <span className="stat__n" style={{ fontSize: "2rem" }}>
                          {counts.get(name) ?? 0}
                        </span>
                      </div>
                      <p className="fine">
                        reported incidents in this class · fault: {spec.fault}
                      </p>
                      <ul className="stack mt-4" style={{ listStyle: "none", padding: 0 }}>
                        {spec.codes.map((code) => (
                          <li key={code} className="row row--between"
                            style={{ borderBottom: "1px solid var(--rule)", paddingBottom: "var(--s2)" }}>
                            <a href={`/tools/incidents?reason_code=${encodeURIComponent(code)}`}>
                              {code.replace(/_/g, " ")}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )
            }}
          </Async>
          <p className="fine mt-6">
            Uncategorised is a real value, not a gap. A reporter who does not know the failure mode
            should not be forced to guess one — that would corrupt every count downstream.
          </p>
        </div>
      </div>
    </Shell>
  )
}

// ── /state-of-agent-risk ───────────────────────────────────────────────────

export function Drop() {
  const drop = useAsync(() => registry.stateOfRisk(), [], "drop")

  return (
    <Shell current="/state-of-agent-risk">
      <div className="band band--acid band--grain">
        <div className="wrap">
          <p className="eyebrow">The periodic drop</p>
          <h1 className="mega mt-4 balance" style={{ maxWidth: "12ch" }}>The State of Agent Risk.</h1>
          <p className="lede mt-6 measure">
            Everything the registry knows, published in full with the methodology beside it so it
            can be quoted without being misquoted.
          </p>
          <p className="mt-6">
            <a className="btn btn--primary btn--lg" href="/state-of-agent-risk.csv">
              Download the dataset (CSV)
            </a>
          </p>
        </div>
      </div>

      <Async state={drop}>
        {(d: StateOfRisk) => (
          <>
            <div className="band band--ink">
              <div className="wrap">
                <div className="grid grid-4">
                  <div className="stat"><div className="stat__n">{d.incidents.total.toLocaleString("en-US")}</div><div className="stat__l">Total reports</div></div>
                  <div className="stat"><div className="stat__n">{d.counted.verified.toLocaleString("en-US")}</div><div className="stat__l">Adjudicated</div></div>
                  <div className="stat"><div className="stat__n">{Math.round(d.verified_share * 100)}%</div><div className="stat__l">Verified share</div></div>
                  <div className="stat"><div className="stat__n">{Math.round(d.resolution_rate * 100)}%</div><div className="stat__l">Marked resolved</div></div>
                </div>
              </div>
            </div>

            <div className="band band--paper">
              <div className="wrap">
                <h2 className="display balance" style={{ maxWidth: "18ch" }}>
                  The corpus, by quarter.
                </h2>
                <p className="fine mt-2 measure">
                  The series rather than a growth multiple. A multiple over two thin quarters is a
                  decimal wearing the costume of a trend — the counts are here and you can decide.
                </p>
                <div className="bars mt-6" style={{ maxWidth: "46rem" }}>
                  {d.by_period.map((p) => (
                    <Bar key={p.period} label={p.period} value={p.filed}
                      sub={`${p.verified} adjudicated`}
                      max={Math.max(...d.by_period.map((x) => x.filed), 1)} />
                  ))}
                </div>

                <h2 className="h2 mt-8">What goes wrong, across everything</h2>
                <div className="bars bars--named mt-5" style={{ maxWidth: "46rem" }}>
                  {d.failure_modes.slice(0, 10).map((m) => (
                    <Bar key={`${m.reason_class}/${m.reason_code}`}
                      label={m.reason_code.replace(/_/g, " ")} sub={m.reason_class}
                      value={m.count} max={Math.max(...d.failure_modes.map((x) => x.count), 1)} />
                  ))}
                </div>

                <div className="slab mt-8">
                  <p className="serif" style={{ fontSize: "var(--t-h2)" }}>
                    And the thing it cannot say: <em>how often this happens.</em>
                  </p>
                  <p className="mt-4">
                    There is no denominator. Nobody publishes how many agent deployments exist, so
                    no rate can be computed from this corpus. Any incident rate you see quoted for
                    AI agents — including one derived from this dataset — was produced by inventing
                    a denominator.
                  </p>
                </div>
              </div>
            </div>

            <div className="band band--bone">
              <div className="wrap">
                <p className="eyebrow">Methodology, in full</p>
                <p className="lede mt-5 measure">{d.methodology}</p>
                <dl className="spec mt-6">
                  <div><dt>Verified claims</dt><dd>{d.health.verified_claims} subjects have a party present to answer</dd></div>
                  <div><dt>Open disputes</dt><dd>{d.health.open_disputes}</dd></div>
                  <div><dt>Excluded from statistics</dt><dd>{d.health.excluded_from_statistics} withdrawn or contested</dd></div>
                  <div><dt>Generated</dt><dd>{d.generated_at}</dd></div>
                </dl>
              </div>
            </div>
          </>
        )}
      </Async>
    </Shell>
  )
}
